import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { Box, ButtonBase, Typography } from "@mui/material";
import type { ImportProgress } from "../../games/games.api";
import { formatAnalysisDuration } from "../analysis-format";
import { layoutTokens } from "@/shared/theme";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";

type AnalysisQueueControlsProps = {
  importProgress: ImportProgress | null;
  isRunning: boolean;
  isLoading: boolean;
  analysisAvailable: boolean;
  entitlementAllowed: boolean;
  entitlementLabel?: string;
  unanalyzedCount: number;
  totalGames: number;
  queueEstimateMs: number;
  onStopImport: () => void;
  onImport: () => void;
  onToggleAnalysis: () => void;
  onEntitlement: () => void;
};

export const AnalysisQueueControls = ({
  importProgress,
  isRunning,
  isLoading,
  analysisAvailable,
  entitlementAllowed,
  entitlementLabel,
  unanalyzedCount,
  totalGames,
  queueEstimateMs,
  onStopImport,
  onImport,
  onToggleAnalysis,
  onEntitlement,
}: AnalysisQueueControlsProps) => {
  const hasNoGames = !isLoading && totalGames === 0 && unanalyzedCount === 0;

  return (
    <Box data-tour="analysis-queue" sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "baseline" },
          justifyContent: "space-between",
          gap: { xs: 0.65, sm: 2 },
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.12em" }}>
            03 / ANALYSIS QUEUE
          </Typography>
          <Typography sx={{ mt: 0.4, fontWeight: 800, fontSize: "1.1rem", letterSpacing: "-0.03em" }}>
            Set Nikola in motion.
          </Typography>
        </Box>
        {!isLoading && (
          <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800 }}>
            {unanalyzedCount} READY · {formatAnalysisDuration(queueEstimateMs, true)}
          </Typography>
        )}
      </Box>
      {isLoading ? (
        <ChesslaLoadingMark label="Nikola is checking your library" />
      ) : (
        <Typography sx={{ color: "text.secondary", fontSize: "0.72rem", mt: 0.75 }}>
          {hasNoGames
            ? "Bring in your first games, then let Nikola get to work."
            : unanalyzedCount === 0
              ? `All ${totalGames} game${totalGames === 1 ? " is" : "s are"} in the archive.`
              : `${unanalyzedCount} game${unanalyzedCount === 1 ? " is" : "s are"} ready for Nikola.`}
        </Typography>
      )}
      <Box
        sx={{
          mt: 1.5,
          display: "grid",
          gridTemplateColumns: importProgress || isRunning ? "1fr" : { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: layoutTokens.controlGap,
        }}
      >
        {importProgress ? (
          <ButtonBase
            onClick={onStopImport}
            data-tour="analysis-stop-import"
            sx={{
              textAlign: "left",
              "&:focus-visible > div": { outline: "2px solid", outlineColor: "secondary.main", outlineOffset: 2 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                minHeight: { xs: 152, sm: 204 },
                p: { xs: 2, sm: 2.5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                bgcolor: "secondary.main",
                color: "background.default",
                border: "1px solid",
                borderColor: "secondary.main",
                "&:hover": { bgcolor: "primary.main", borderColor: "primary.main" },
              }}
            >
              <StopIcon sx={{ fontSize: { xs: "2rem", sm: "2.5rem" } }} />
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", letterSpacing: "0.04em" }}>
                  STOP IMPORT
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: "0.68rem" }}>
                  {importProgress.imported}
                  {importProgress.target != null ? ` / ${importProgress.target}` : ""} games brought in · Nikola is
                  searching further back.
                </Typography>
              </Box>
            </Box>
          </ButtonBase>
        ) : (
          <>
            {!isRunning && (
              <ButtonBase
                onClick={onImport}
                data-tour="analysis-import"
                sx={{
                  textAlign: "left",
                  "&:focus-visible > div": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    minHeight: { xs: 152, sm: 204 },
                    p: { xs: 2, sm: 2.5 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "none",
                    "&:hover": {
                      bgcolor: "primary.main",
                      borderColor: "primary.main",
                      color: "background.default",
                      "& .queue-action-detail": { color: "rgba(10,10,10,0.68)" },
                    },
                  }}
                >
                  <DownloadIcon sx={{ fontSize: { xs: "2rem", sm: "2.5rem" } }} />
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", letterSpacing: "0.04em" }}>
                      IMPORT GAMES
                    </Typography>
                    <Typography
                      className="queue-action-detail"
                      sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.4 }}
                    >
                      Import all Chess.com games matching Step 2.
                    </Typography>
                  </Box>
                </Box>
              </ButtonBase>
            )}
            <ButtonBase
              data-tour={isRunning ? "analysis-stop" : "analysis-start"}
              disabled={
                isLoading ||
                (!entitlementAllowed && !entitlementLabel) ||
                (entitlementAllowed && (!analysisAvailable || (!isRunning && unanalyzedCount === 0)))
              }
              onClick={entitlementAllowed ? onToggleAnalysis : onEntitlement}
              sx={{
                textAlign: "left",
                "&.Mui-disabled": { opacity: 1 },
                "&:focus-visible > div": {
                  outline: "2px solid",
                  outlineColor: isRunning ? "secondary.main" : "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  minHeight: { xs: 152, sm: 204 },
                  p: { xs: 2, sm: 2.5 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  bgcolor: isRunning ? "secondary.main" : "background.paper",
                  color: isRunning ? "background.default" : "text.primary",
                  border: "1px solid",
                  borderColor: isRunning ? "secondary.main" : "divider",
                  transition: "none",
                  "&:hover":
                    !isLoading && (isRunning || unanalyzedCount > 0)
                      ? {
                          bgcolor: "primary.main",
                          borderColor: "primary.main",
                          color: "background.default",
                          "& .queue-action-detail": { color: "rgba(10,10,10,0.68)" },
                        }
                      : undefined,
                }}
              >
                {isRunning ? (
                  <StopIcon sx={{ fontSize: { xs: "2rem", sm: "2.5rem" } }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: { xs: "2rem", sm: "2.5rem" } }} />
                )}
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", letterSpacing: "0.04em" }}>
                    {isRunning
                      ? "STOP ANALYSIS"
                      : entitlementAllowed
                        ? "START ANALYSIS"
                        : (entitlementLabel ?? "CHECKING ACCESS…")}
                  </Typography>
                  <Typography
                    className="queue-action-detail"
                    sx={{
                      mt: 0.5,
                      color: isRunning ? "rgba(10,10,10,0.68)" : "text.secondary",
                      fontSize: "0.68rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {isRunning
                      ? "Nikola is already in the engine room."
                      : !entitlementAllowed
                        ? "Choose the games available for saved analysis."
                        : !analysisAvailable
                          ? "This browser cannot run the local engine."
                          : unanalyzedCount > 0
                            ? "Run the selected depth on every waiting game."
                            : "Import a game to unlock the queue."}
                  </Typography>
                </Box>
              </Box>
            </ButtonBase>
          </>
        )}
      </Box>
    </Box>
  );
};
